import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSuccess(true);
    toast.success('Mensagem enviada com sucesso!');

    // Reset after 3 seconds
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        employees: '',
        subject: '',
        message: '',
      });
    }, 3000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-mail',
      content: 'contato@cspcursos.com.br',
      link: 'mailto:contato@cspcursos.com.br',
    },
    {
      icon: Phone,
      title: 'Telefone',
      content: '(00) 0000-0000',
      link: 'tel:+5500000000000',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      content: '(00) 00000-0000',
      link: 'https://wa.me/5500000000000',
    },
    {
      icon: MapPin,
      title: 'Endereço',
      content: 'São Paulo, SP - Brasil',
      link: null,
    },
    {
      icon: Clock,
      title: 'Horário',
      content: 'Seg-Sex: 9h às 18h',
      link: null,
    },
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
              Fale Conosco
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Vamos conversar sobre sua{' '}
              <span className="text-blue-600">empresa</span>?
            </h1>
            <p className="text-lg text-slate-600">
              Estamos prontos para entender suas necessidades e apresentar 
              as melhores soluções de educação corporativa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Informações de Contato
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">{info.title}</p>
                      {info.link ? (
                        <a
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-slate-900 hover:text-blue-600 transition-colors font-medium"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-slate-900 font-medium">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick CTA */}
              <Card className="mt-8 p-6 bg-orange-50 border-orange-100">
                <CardContent className="p-0">
                  <h3 className="font-semibold text-slate-900 mb-2">
                    Diagnóstico Gratuito
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Agende uma conversa com nossos especialistas para 
                    um diagnóstico gratuito das suas necessidades.
                  </p>
                  <a
                    href="https://wa.me/5500000000000"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-orange-600 font-medium hover:text-orange-700"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chamar no WhatsApp
                  </a>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <Card className="p-8 shadow-lg border-0">
                <CardContent className="p-0">
                  {isSuccess ? (
                    <div className="py-12 text-center">
                      <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">
                        Mensagem Enviada!
                      </h3>
                      <p className="text-slate-600">
                        Obrigado pelo contato. Retornaremos em breve.
                      </p>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-900 mb-2">
                        Envie uma mensagem
                      </h2>
                      <p className="text-slate-600 mb-8">
                        Preencha o formulário abaixo e entraremos em contato em até 24h.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name">Nome completo *</Label>
                            <Input
                              id="name"
                              data-testid="contact-input-name"
                              placeholder="Seu nome"
                              value={formData.name}
                              onChange={(e) => handleChange('name', e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">E-mail corporativo *</Label>
                            <Input
                              id="email"
                              type="email"
                              data-testid="contact-input-email"
                              placeholder="seu@empresa.com.br"
                              value={formData.email}
                              onChange={(e) => handleChange('email', e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Telefone/WhatsApp *</Label>
                            <Input
                              id="phone"
                              data-testid="contact-input-phone"
                              placeholder="(00) 00000-0000"
                              value={formData.phone}
                              onChange={(e) => handleChange('phone', e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company">Empresa *</Label>
                            <Input
                              id="company"
                              data-testid="contact-input-company"
                              placeholder="Nome da empresa"
                              value={formData.company}
                              onChange={(e) => handleChange('company', e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="employees">Número de colaboradores</Label>
                            <Select
                              value={formData.employees}
                              onValueChange={(value) => handleChange('employees', value)}
                            >
                              <SelectTrigger data-testid="contact-select-employees">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1-50">1-50</SelectItem>
                                <SelectItem value="51-200">51-200</SelectItem>
                                <SelectItem value="201-500">201-500</SelectItem>
                                <SelectItem value="501-1000">501-1.000</SelectItem>
                                <SelectItem value="1001-5000">1.001-5.000</SelectItem>
                                <SelectItem value="5000+">Mais de 5.000</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subject">Assunto *</Label>
                            <Select
                              value={formData.subject}
                              onValueChange={(value) => handleChange('subject', value)}
                            >
                              <SelectTrigger data-testid="contact-select-subject">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="plataforma-ead">Plataforma EAD</SelectItem>
                                <SelectItem value="producao-cursos">Produção de Cursos</SelectItem>
                                <SelectItem value="nr01">NR-01 Psicossocial</SelectItem>
                                <SelectItem value="pacote-completo">Pacote Completo</SelectItem>
                                <SelectItem value="parceria">Parceria</SelectItem>
                                <SelectItem value="outro">Outro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Mensagem *</Label>
                          <Textarea
                            id="message"
                            data-testid="contact-textarea-message"
                            placeholder="Conte-nos mais sobre suas necessidades..."
                            rows={5}
                            value={formData.message}
                            onChange={(e) => handleChange('message', e.target.value)}
                            required
                          />
                        </div>

                        <Button
                          type="submit"
                          data-testid="contact-submit-btn"
                          disabled={isSubmitting}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 rounded-lg font-semibold text-lg"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-5 w-5" />
                              Enviar Mensagem
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section (placeholder) */}
      <section className="h-96 bg-slate-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500">Mapa em breve</p>
          </div>
        </div>
      </section>
    </div>
  );
};
